import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import { Download } from 'lucide-react';

const ReportGenerator = ({ reportType, data, dateRange, companyDetails }) => {
  const [logoBase64, setLogoBase64] = useState(null);
  
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch('/logo.jpg');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (error) {
        console.log('Logo not found, proceeding without logo');
      }
    };
    loadLogo();
  }, []);
  
  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Get company details with proper fallback chain
    let company = companyDetails;
    if (!company || Object.keys(company).length === 0) {
      const stored = localStorage.getItem('addictCompany');
      company = stored ? JSON.parse(stored) : {
        name: 'ADDICT',
        address: '123 Business Street',
        city: 'Colombo, Sri Lanka',
        phone: '+94 11 234 5678',
        email: 'info@company.lk'
      };
    }
    
    // Professional Header Background
    doc.setFillColor(102, 126, 234);
    doc.rect(0, 0, 210, 45, 'F');
    
    // Add logo if available
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', 15, 10, 25, 25);
      } catch (error) {
        console.log('Error adding logo to PDF:', error);
      }
    }
    
    // Company Details - Right aligned
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const rightX = 195;
    doc.text(company.address, rightX, 15, { align: 'right' });
    doc.text(company.city, rightX, 22, { align: 'right' });
    doc.text(company.phone, rightX, 29, { align: 'right' });
    doc.text(company.email, rightX, 36, { align: 'right' });
    
    // Report Title
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    let title = '';
    switch(reportType) {
      case 'inventory': title = 'Inventory Report'; break;
      case 'sales': title = 'Sales Report'; break;
      case 'orders': title = 'Orders Report'; break;
      case 'customers': title = 'Customers Report'; break;
      default: title = 'Report';
    }
    doc.text(title, 15, 60);
    
    // Date Range
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const dateText = dateRange.start && dateRange.end 
      ? `Period: ${new Date(dateRange.start).toLocaleDateString()} - ${new Date(dateRange.end).toLocaleDateString()}`
      : `Generated: ${new Date().toLocaleDateString()}`;
    doc.text(dateText, 15, 68);
    
    let yPos = 80;
    
    if (reportType === 'inventory') {
      generateInventoryReport(doc, data, yPos);
    } else if (reportType === 'sales') {
      generateSalesReport(doc, data, yPos);
    } else if (reportType === 'orders') {
      generateOrdersReport(doc, data, yPos);
    } else if (reportType === 'customers') {
      generateCustomersReport(doc, data, yPos);
    }
    
    return doc;
  };
  
  const generateInventoryReport = (doc, inventory, startY) => {
    let yPos = startY;
    
    // Summary
    const totalItems = inventory.length;
    const totalPacks = inventory.reduce((sum, item) => sum + (item.packsInStock || 0), 0);
    const totalPairs = inventory.reduce((sum, item) => sum + ((item.packsInStock * item.pairsPerPack) + (item.loosePairs || 0)), 0);
    const totalValue = inventory.reduce((sum, item) => sum + (((item.packsInStock * item.pairsPerPack) + (item.loosePairs || 0)) * item.pricePerPair), 0);
    
    doc.setFillColor(248, 249, 250);
    doc.rect(15, yPos, 180, 25, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Summary: ${totalItems} Items | ${totalPacks} Packs | ${totalPairs} Pairs | LKR ${totalValue.toLocaleString()}`, 20, yPos + 8);
    doc.text(`Average Value per Item: LKR ${Math.round(totalValue / totalItems).toLocaleString()}`, 20, yPos + 18);
    
    yPos += 35;
    
    // Table Header
    doc.setFillColor(102, 126, 234);
    doc.rect(15, yPos, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('ARTICLE', 20, yPos + 5);
    doc.text('COLOUR', 50, yPos + 5);
    doc.text('PACKS', 80, yPos + 5);
    doc.text('PAIRS', 100, yPos + 5);
    doc.text('PRICE/PAIR', 120, yPos + 5);
    doc.text('TOTAL VALUE', 150, yPos + 5);
    
    yPos += 10;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    
    inventory.forEach((item, index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 30;
      }
      
      const totalPairs = (item.packsInStock * item.pairsPerPack) + (item.loosePairs || 0);
      const itemValue = totalPairs * item.pricePerPair;
      
      const bgColor = index % 2 === 0 ? [255, 255, 255] : [248, 249, 250];
      doc.setFillColor(...bgColor);
      doc.rect(15, yPos - 2, 180, 6, 'F');
      
      doc.text(item.articleNumber, 20, yPos + 2);
      doc.text(item.colour, 50, yPos + 2);
      doc.text(item.packsInStock.toString(), 80, yPos + 2);
      doc.text(totalPairs.toString(), 100, yPos + 2);
      doc.text(`LKR ${item.pricePerPair}`, 120, yPos + 2);
      doc.text(`LKR ${itemValue.toLocaleString()}`, 150, yPos + 2);
      
      yPos += 6;
    });
  };
  
  const generateSalesReport = (doc, salesData, startY) => {
    let yPos = startY;
    const { sales, totalSales, totalRevenue, totalDiscount } = salesData;
    
    // Summary
    doc.setFillColor(248, 249, 250);
    doc.rect(15, yPos, 180, 25, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Summary: ${totalSales} Sales | Revenue: LKR ${totalRevenue.toLocaleString()} | Discounts: LKR ${totalDiscount.toLocaleString()}`, 20, yPos + 8);
    doc.text(`Average Sale Value: LKR ${Math.round(totalRevenue / totalSales).toLocaleString()}`, 20, yPos + 18);
    
    yPos += 35;
    
    // Table Header
    doc.setFillColor(102, 126, 234);
    doc.rect(15, yPos, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('SALE ID', 20, yPos + 5);
    doc.text('CUSTOMER', 45, yPos + 5);
    doc.text('DATE', 85, yPos + 5);
    doc.text('ITEMS', 110, yPos + 5);
    doc.text('TOTAL', 130, yPos + 5);
    doc.text('PAYMENT', 160, yPos + 5);
    
    yPos += 10;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    
    sales.forEach((sale, index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 30;
      }
      
      const bgColor = index % 2 === 0 ? [255, 255, 255] : [248, 249, 250];
      doc.setFillColor(...bgColor);
      doc.rect(15, yPos - 2, 180, 6, 'F');
      
      doc.text(`#${String(sale.id).slice(-6)}`, 20, yPos + 2);
      doc.text(sale.customerName.substring(0, 15), 45, yPos + 2);
      doc.text(new Date(sale.createdAt).toLocaleDateString(), 85, yPos + 2);
      doc.text((sale.items?.length || 0).toString(), 110, yPos + 2);
      doc.text(`LKR ${(sale.total || 0).toLocaleString()}`, 130, yPos + 2);
      doc.text(sale.paymentStatus || 'N/A', 160, yPos + 2);
      
      yPos += 6;
    });
  };
  
  const generateOrdersReport = (doc, orders, startY) => {
    let yPos = startY;
    
    const totalOrders = orders.length;
    const totalValue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
    const statusCounts = orders.reduce((acc, order) => {
      acc[order.status] = (acc[order.status] || 0) + 1;
      return acc;
    }, {});
    
    // Summary
    doc.setFillColor(248, 249, 250);
    doc.rect(15, yPos, 180, 25, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Summary: ${totalOrders} Orders | Total Value: LKR ${totalValue.toLocaleString()}`, 20, yPos + 8);
    doc.text(`Status: Pending(${statusCounts.pending || 0}) | Ready(${statusCounts.ready || 0}) | Completed(${statusCounts.completed || 0})`, 20, yPos + 18);
    
    yPos += 35;
    
    // Table Header
    doc.setFillColor(102, 126, 234);
    doc.rect(15, yPos, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('ORDER ID', 20, yPos + 5);
    doc.text('CUSTOMER', 50, yPos + 5);
    doc.text('DATE', 90, yPos + 5);
    doc.text('ITEMS', 115, yPos + 5);
    doc.text('STATUS', 135, yPos + 5);
    doc.text('TOTAL', 165, yPos + 5);
    
    yPos += 10;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    
    orders.forEach((order, index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 30;
      }
      
      const bgColor = index % 2 === 0 ? [255, 255, 255] : [248, 249, 250];
      doc.setFillColor(...bgColor);
      doc.rect(15, yPos - 2, 180, 6, 'F');
      
      doc.text(`#${String(order.id).slice(-6)}`, 20, yPos + 2);
      doc.text(order.customerName.substring(0, 12), 50, yPos + 2);
      doc.text(new Date(order.createdAt).toLocaleDateString(), 90, yPos + 2);
      doc.text((order.items?.length || 0).toString(), 115, yPos + 2);
      doc.text(order.status.toUpperCase(), 135, yPos + 2);
      doc.text(`LKR ${(order.total || 0).toLocaleString()}`, 165, yPos + 2);
      
      yPos += 6;
    });
  };
  
  const generateCustomersReport = (doc, customers, startY) => {
    let yPos = startY;
    
    const totalCustomers = customers.length;
    const avgOrdersPerCustomer = customers.reduce((sum, c) => sum + (c.orderCount || 0), 0) / totalCustomers;
    
    // Summary
    doc.setFillColor(248, 249, 250);
    doc.rect(15, yPos, 180, 25, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Summary: ${totalCustomers} Customers | Avg Orders per Customer: ${avgOrdersPerCustomer.toFixed(1)}`, 20, yPos + 8);
    
    yPos += 35;
    
    // Table Header
    doc.setFillColor(102, 126, 234);
    doc.rect(15, yPos, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('SHOP NAME', 20, yPos + 5);
    doc.text('CITY', 70, yPos + 5);
    doc.text('PHONE', 100, yPos + 5);
    doc.text('ORDERS', 130, yPos + 5);
    doc.text('TOTAL SPENT', 155, yPos + 5);
    
    yPos += 10;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    
    customers.forEach((customer, index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 30;
      }
      
      const bgColor = index % 2 === 0 ? [255, 255, 255] : [248, 249, 250];
      doc.setFillColor(...bgColor);
      doc.rect(15, yPos - 2, 180, 6, 'F');
      
      doc.text(customer.shopName.substring(0, 20), 20, yPos + 2);
      doc.text(customer.city, 70, yPos + 2);
      doc.text(customer.phoneNumber || 'N/A', 100, yPos + 2);
      doc.text((customer.orderCount || 0).toString(), 130, yPos + 2);
      doc.text(`LKR ${(customer.totalSpent || 0).toLocaleString()}`, 155, yPos + 2);
      
      yPos += 6;
    });
  };
  
  const handleDownload = () => {
    const doc = generatePDF();
    const fileName = `${reportType}-report-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };
  
  return (
    <button 
      onClick={handleDownload}
      className="btn btn-primary"
      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
    >
      <Download size={16} />
      Download PDF Report
    </button>
  );
};

export default ReportGenerator;